<script lang="ts" setup>
import {
  ref,
  useAuth,
  useDialog,
  useRoute,
  computed,
  useDraw,
  onNuxtReady,
} from '#imports';

import { useMediaQuery } from '@vueuse/core'

const route = useRoute();
const { openDialog, setDialogFullscreen } = useDialog();
const { currentUser, fetchCurrentUser, headerStyle } = useAuth();
fetchCurrentUser();
const { toggleDraw } = useDraw();

const appReady = ref<boolean>(false);
onNuxtReady(() => {
  appReady.value = true;
});

const isStudio = computed(() => route.path.startsWith('/studio'));
const isLessThanSm = useMediaQuery<string>('(max-width: 920px)');

const handleGetPremiumClick = () => {
  openDialog('MPPricing');
  setDialogFullscreen(true)
};
</script>
<template>
  <header v-if="!isStudio" :class="[headerStyle, {'app-ready': appReady }]">
    <NuxtLinkLocale to="/" class="logo">
      <img src="/logos/logo.png" alt="Logo" class="circle" />
      <MPLogo class="full"/>
      <MPSearchInline placeholder="What are you looking for?" class="search-input" />
    </NuxtLinkLocale>
    <div class="menu">
      <NuxtLinkLocale
        to="/"
        exactActiveClass="isActive"
      >
        <i class="ri-home-fill"/>
        <span>Home</span>
      </NuxtLinkLocale>
      <NuxtLinkLocale
        to="/explore"
        exactActiveClass="isActive"
        :class="{'isActive': route.path.startsWith('/explore') || route.path.startsWith('/maps')}"
      >
        <i class="ri-compass-line"/>
        <span>Explore</span>
      </NuxtLinkLocale>
      <NuxtLinkLocale
        to="/atlas"
        exactActiveClass="isActive"
      >
        <i class="ri-earth-line"/>
        <span>Atlas</span>
      </NuxtLinkLocale>
      <NuxtLinkLocale
        to="/creators"
        exactActiveClass="isActive"
      >
        <i class="ri-user-community-fill"/>
        <span>Creators</span>
      </NuxtLinkLocale>
      <a @click="handleGetPremiumClick" class="premium-link">
        <span>Get Premium</span>
        <i class="ri-sparkling-line"/>
      </a>
      <a v-if="!currentUser" aria-label="Login" class="profile-link" @click="openDialog('MPAuth', { startOn: 'login', title: 'Join Mappr' })">
        <i class="ri-account-circle-2-line"/>
        <span>Profile</span>
      </a>
      <a v-else aria-label="Profile" class="profile-link user-menu" @click="toggleDraw">
        <MPImg
          v-if="currentUser?.profile?.profile_image_url && !isLessThanSm"
          :width="90"
          :height="90"
          :src="currentUser?.profile?.profile_image_url"
          class="user-dropdown-trigger-image"
        />
        <i v-if="isLessThanSm || !currentUser?.profile?.profile_image_url" class="ri-account-circle-2-line"/>
      </a>
    </div>
  </header>
</template>

<style lang="postcss" scoped>
@import '@/assets/css/breakpoints.pcss';
@import '@/assets/css/layouts.pcss';

header {
  z-index: 8000;
  padding: 1rem;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  @mixin screen sm {
    display: flex;
    @mixin layout-wrapper-wider;
    padding: 0.75rem 0;
    gap: 0.75rem;
  }

  @mixin screen lg {
    @mixin layout-wrapper-full;
  }
}

header.app-ready {
  opacity: 1;
  transition: opacity 0.3s ease-in-out;
}

/*======
Logo
======*/
.logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:deep(input::placeholder) {
    font-size: 1rem;
  }
  &:deep(input) {
    padding-top: 0.6rem;
    padding-bottom: 0.6rem;
    border-radius: 2rem;
  }
}

.logo .circle {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 100%;
  display: block;
  @mixin screen sm {
    display: none;
  }
}

.logo .full {
  width: auto;
  height: 2rem;
  display: none;
  @mixin screen sm {
    display: block;
  }
}

/*
======
Search
======
*/
.search-input {
  @mixin screen sm {
    display: none;
  }
}

/*
======
Menu
======
*/
.menu {
  z-index: 8001;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 300ms background-color ease-in-out;
  background-color: var(--color-slate-50);
  color: var(--color-slate-600);
  box-shadow: var(--shadow-md);
  @mixin screen sm {
    position: relative;
    top: 0;
    background-color: transparent;
    box-shadow: none;
    color: var(--color-slate-600);
    width: 100%;
  }
}

.menu a {
  position: relative;
  padding: 1rem 1.25rem 1.25rem;
  display: grid;
  align-items: center;
  justify-content: center;
  text-align: center;
  transition: 50ms all ease-in-out;
  @mixin screen sm {
    padding: 0.5rem;
    gap: 0.25rem;
    grid-template-columns: auto 1fr;
  }
}

.menu i {
  font-size: 1.25rem;
}

.menu span {
  position: absolute;
  bottom: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  transition: 50ms all ease-in-out;
  transition-delay: 70ms;
  opacity: 0;
  font-size: 0.8rem;
  @mixin screen sm {
    position: relative;
    bottom: -0.045rem;
    opacity: 1;
    font-size: 0.95rem;
  }
}

.menu .profile-link {
  cursor: pointer;
  @mixin screen sm {
    padding: 0;
    gap: 0;
  }
}

.menu .profile-link i {
  @mixin screen sm {
    font-size: 2.35rem;
    padding-left: 0.25rem;
  }
}

.menu .profile-link img {
  width: 2.5rem;
  height: 2.5rem;
  object-fit: cover;
  border-radius: 100%;
  margin-left: 0.5rem;
}

.menu .profile-link span {
  @mixin screen sm {
    display: none;
  }
}

.menu .premium-link {
  cursor: pointer;
  display: none;
  @mixin screen sm {
    display: grid;
    position: relative;
    margin-left: auto;
    padding: 0.5rem 1.25rem;
    background-color: var(--color-slate-100);
    border-radius: 1rem;
  }
}

.menu .premium-link i {
  color: var(--color-yellow-400);
  position: absolute;
  top: -0.6rem;
  right: 0;
}

/*
======
State
======
*/
header.transparent .menu {
  background-color: transparent;
  color: var(--color-slate-50);
  @mixin screen sm {
    color: inherit;
  }
}

header.inverted .menu {
  background-color: transparent;
  color: var(--color-slate-50);
}

header.inverted .logo:deep(path) {
  @mixin screen sm {
    fill: var(--color-slate-50);
  }
}

header.inverted .premium-link {
  color: var(--color-slate-600);
}

.menu a.isActive {
  transition: 50ms all ease-in-out;
  color: var(--color-rose-400);
}

.menu a.isActive i {
  position: relative;
  top: -0.25rem;
  @mixin screen sm {
    top: 0;
  }
}

.menu a.isActive span {
  color: var(--color-rose-400);
  transition: 50ms all ease-in-out;
  transition-delay: 70ms;
  opacity: 1;
}

.menu .premium-link:hover i {
  color: var(--color-yellow-300);
  position: absolute;
  top: -0.6rem;
  right: 0;
}

.menu .premium-link:hover {
  background-color: var(--color-slate-200);
}
</style>
